/**
 * 🔔 NotificationsPage — full-page notification center.
 *
 * Canon: /app/memory/NOTIFICATIONS_CANON_FEB2026.md
 * Complements the header bell dropdown (which shows the recent 5).
 * Adds: pagination up to 100, all/unread filter, per-item mark-read + delete,
 *       and an owner-only "📢 Broadcast" panel that fans a message out to
 *       every user via /api/notifications/owner/broadcast.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Trash2, Megaphone, X as XIcon } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function OwnerBroadcastModal({ open, onClose, onSent }) {
  const [title, setTitle] = useState("");
  const [body, setBody]   = useState("");
  const [link, setLink]   = useState("");
  const [icon, setIcon]   = useState("📢");
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const send = async () => {
    if (!title.trim()) { toast.error("Title is required."); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/notifications/owner/broadcast`, {
        title: title.trim(),
        body:  body.trim(),
        link:  link.trim() || undefined,
        icon:  icon.trim() || undefined,
      });
      toast.success(`Broadcast sent to ${data.sent} users`);
      setTitle(""); setBody(""); setLink(""); setIcon("📢");
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
         data-testid="owner-broadcast-modal">
      <div className="w-full max-w-lg rounded-2xl border border-amber-600/50 bg-slate-950 p-5 shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-bold text-amber-100">📢 Owner Broadcast</h3>
          <button onClick={onClose} data-testid="broadcast-close">
            <XIcon className="h-5 w-5 text-neutral-400 hover:text-white" />
          </button>
        </div>
        <p className="mb-3 text-xs text-amber-200/80">
          Sends this notification to every user. You&apos;re excluded automatically.
        </p>
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-amber-300">Icon</span>
            <input value={icon} onChange={(e) => setIcon(e.target.value)}
                   maxLength={4}
                   data-testid="broadcast-icon"
                   className="w-16 rounded border border-amber-800/60 bg-black/40 p-2 text-center text-lg text-white" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-amber-300">Title *</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
                   maxLength={200}
                   data-testid="broadcast-title"
                   placeholder="Ex: New event live!"
                   className="w-full rounded border border-amber-800/60 bg-black/40 p-2 text-sm text-white" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-amber-300">Body</span>
            <textarea value={body} onChange={(e) => setBody(e.target.value)}
                      maxLength={1000} rows={3}
                      data-testid="broadcast-body"
                      placeholder="Details for your players…"
                      className="w-full rounded border border-amber-800/60 bg-black/40 p-2 text-sm text-white" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-amber-300">Link (optional)</span>
            <input value={link} onChange={(e) => setLink(e.target.value)}
                   placeholder="/bazaar"
                   data-testid="broadcast-link"
                   className="w-full rounded border border-amber-800/60 bg-black/40 p-2 text-sm font-mono text-white" />
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose}
                  className="rounded border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-neutral-300">
            Cancel
          </button>
          <button onClick={send} disabled={sending}
                  data-testid="broadcast-send"
                  className="rounded border border-amber-600 bg-gradient-to-br from-amber-700 to-amber-500 px-4 py-2 text-sm font-bold text-black disabled:opacity-50">
            {sending ? "Sending…" : "📢 Send to Everyone"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all"); // all | unread
  const [loading, setLoading] = useState(true);
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  const isOwner = user?.role === "owner";

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/notifications?limit=100${filter === "unread" ? "&only_unread=true" : ""}`;
      const { data } = await axios.get(url);
      setItems(data.notifications || data.items || data || []);
    } catch (e) {
      toast.error("Couldn't load notifications");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { refresh(); }, [refresh]);

  const markRead = async (n) => {
    try {
      await axios.post(`${API}/notifications/${n.id}/read`);
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
    } catch (e) { /* silent */ }
  };

  const openItem = async (n) => {
    if (!n.read) await markRead(n);
    if (n.link) navigate(n.link);
  };

  const del = async (n) => {
    try {
      await axios.delete(`${API}/notifications/${n.id}`);
      setItems((prev) => prev.filter((x) => x.id !== n.id));
    } catch (e) { toast.error("Delete failed"); }
  };

  const markAll = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      toast.success("All marked read");
    } catch (e) { toast.error("Failed"); }
  };

  const unreadCount = items.filter((x) => !x.read).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-purple-950/20 to-black text-purple-50">
      <Header />
      <div className="mx-auto max-w-3xl px-4 pt-20 pb-24 md:pt-24" data-testid="notifications-page">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 font-serif text-4xl font-bold text-purple-100 sm:text-5xl">
              <Bell className="h-7 w-7 text-purple-300" /> Notifications
            </h1>
            <p className="mt-1 text-sm text-purple-300/80">
              {unreadCount > 0
                ? `${unreadCount} unread · latest 100 shown here`
                : "All caught up. Latest 100 shown here."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isOwner && (
              <button
                onClick={() => setBroadcastOpen(true)}
                data-testid="open-broadcast-btn"
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-600/60 bg-amber-900/40 px-3 py-1.5 text-xs font-semibold text-amber-100 hover:bg-amber-800/50"
              >
                <Megaphone className="h-4 w-4" /> Broadcast
              </button>
            )}
            <button
              onClick={markAll}
              disabled={unreadCount === 0}
              data-testid="mark-all-read-btn"
              className="inline-flex items-center gap-1.5 rounded-lg border border-purple-700/60 bg-purple-900/40 px-3 py-1.5 text-xs font-semibold text-purple-100 hover:bg-purple-800/50 disabled:opacity-40"
            >
              <Check className="h-4 w-4" /> Mark all read
            </button>
          </div>
        </div>

        <div className="mb-4 flex gap-2" data-testid="notifications-filter">
          {[["all", "All"], ["unread", `Unread${unreadCount ? ` (${unreadCount})` : ""}`]].map(([k, label]) => (
            <button
              key={k}
              data-testid={`filter-${k}`}
              onClick={() => setFilter(k)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                filter === k
                  ? "border-purple-400 bg-purple-800/60 text-white"
                  : "border-purple-800/40 bg-purple-950/30 text-purple-200 hover:bg-purple-900/40"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-16 text-center text-purple-300">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-purple-800/40 bg-purple-950/30 p-10 text-center">
            <div className="mb-2 text-4xl">🌱</div>
            <div className="text-lg font-semibold text-purple-100">
              {filter === "unread" ? "No unread notifications." : "Nothing yet."}
            </div>
            <div className="mt-1 text-sm text-purple-300/80">
              Sell at the Bazaar, hit a streak, or clear an Arena run — this page will fill up.
            </div>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="notifications-list">
            {items.map((n) => (
              <li
                key={n.id}
                data-testid={`notification-${n.id}`}
                className={`flex items-start gap-3 rounded-xl border p-3 transition-colors ${
                  n.read
                    ? "border-purple-900/30 bg-purple-950/20 opacity-70"
                    : "border-purple-600/50 bg-purple-950/50 shadow-[0_0_20px_-8px_rgba(168,85,247,0.5)]"
                }`}
              >
                <div className="text-2xl leading-none">{n.icon || "🔔"}</div>
                <div className="flex-1 cursor-pointer" onClick={() => openItem(n)}>
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${n.read ? "text-purple-200" : "text-purple-50"}`}>
                      {n.title}
                    </span>
                    {!n.read && <span className="h-2 w-2 rounded-full bg-purple-400" />}
                  </div>
                  {n.body && <div className="mt-0.5 text-xs text-purple-300/90">{n.body}</div>}
                  <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-wider text-purple-400/70">
                    <span>{n.kind}</span>
                    <span>·</span>
                    <span>{timeAgo(n.created_at)}</span>
                    {n.link && <span>· ↗ {n.link}</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  {!n.read && (
                    <button
                      onClick={() => markRead(n)}
                      title="Mark read"
                      data-testid={`mark-read-${n.id}`}
                      className="rounded p-1 text-purple-300 hover:bg-purple-900/40 hover:text-white"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => del(n)}
                    title="Delete"
                    data-testid={`delete-${n.id}`}
                    className="rounded p-1 text-red-400 hover:bg-red-900/40 hover:text-red-200"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <OwnerBroadcastModal
        open={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
        onSent={refresh}
      />
      <Footer />
    </div>
  );
}
